import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Seo from '@/components/base/Seo';
import { categoryList, newsList, siteInfo } from '@/mocks/news';
import { faqList } from '@/mocks/site';
import HeroSection from '@/pages/home/components/HeroSection';
import LiveTicker from '@/pages/home/components/LiveTicker';
import CategoryFilter from '@/pages/home/components/CategoryFilter';
import FeedList from '@/pages/home/components/FeedList';
import HomeAside from '@/pages/home/components/HomeAside';
import FaqSection from '@/pages/home/components/FaqSection';
import BrandIntro from '@/pages/home/components/BrandIntro';

const PAGE_SIZE = 4;

export default function Home() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeCat = searchParams.get('cat') ?? 'all';
  const keyword = searchParams.get('q') ?? '';
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [activeCat, keyword]);

  const counts = useMemo(() => {
    const base: Record<string, number> = { all: newsList.length };
    categoryList.forEach((cat) => {
      if (cat.id !== 'all') {
        base[cat.id] = newsList.filter((item) => item.categoryId === cat.id).length;
      }
    });
    return base;
  }, []);

  const filtered = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    return newsList.filter((item) => {
      const matchCat = activeCat === 'all' || item.categoryId === activeCat;
      if (!key) return matchCat;
      const matchKey =
        item.title.toLowerCase().includes(key) ||
        item.excerpt.toLowerCase().includes(key) ||
        item.categoryName.includes(key) ||
        item.tags.some((tag) => tag.toLowerCase().includes(key));
      return matchCat && matchKey;
    });
  }, [activeCat, keyword]);

  const handleCategory = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'all') next.delete('cat');
    else next.set('cat', id);
    const qs = next.toString();
    navigate(`/${qs ? `?${qs}` : ''}#feed`);
  };

  const clearKeyword = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('q');
    const qs = next.toString();
    navigate(`/${qs ? `?${qs}` : ''}#feed`);
  };

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: siteInfo.name,
      url: 'https://utuyxa.cn/',
      description: siteInfo.description,
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://utuyxa.cn/?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: '今日新瓜实时更新列表',
      itemListElement: newsList.slice(0, 8).map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://utuyxa.cn/article/${item.id}`,
        name: item.title,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqList.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    },
  ];

  return (
    <>
      <Seo
        title="黑料爆料 | 今日新瓜实时更新抢先看"
        description="黑料爆料实时更新今日新瓜与首发爆料，按时间与热度整理娱乐圈、网红圈、综艺、影视、电竞等九大圈子动态，打开首页即可抢先看最新时间线。"
        keywords="黑料爆料,黑料爆料今日,黑料爆料新瓜,黑料爆料首发,黑料爆料实时更新"
        path="/"
        jsonLd={jsonLd}
      />

      <HeroSection />
      <LiveTicker />

      <section id="feed" className="w-full scroll-mt-20">
        <div className="mx-auto w-full px-4 py-14 md:px-6 md:py-16">
          <div className="flex flex-col gap-6 border-b border-background-200 pb-8 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-primary-500/40 bg-primary-500/10 px-3 py-1.5 text-xs font-label text-primary-400">
                <i className="ri-fire-line" />
                今日新瓜 · 实时更新
              </span>
              <h2 className="mt-4 font-heading text-3xl text-foreground-950 md:text-4xl">
                最新情报流
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
                刚出的料直接摆在首页，按热度与时间双重排序。切换圈子或搜索关键词，列表会实时刷新。
              </p>
            </div>
            <p className="text-sm text-foreground-500">
              当前筛选：<span className="text-accent-400">{counts[activeCat] ?? 0}</span> 条
              {keyword ? (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700">
                  “{keyword}”
                  <button
                    type="button"
                    onClick={clearKeyword}
                    aria-label="清除关键词"
                    className="cursor-pointer text-foreground-500 hover:text-primary-400"
                  >
                    <i className="ri-close-line" />
                  </button>
                </span>
              ) : null}
            </p>
          </div>

          <div className="mt-8">
            <CategoryFilter active={activeCat} counts={counts} onChange={handleCategory} />
          </div>

          <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <FeedList
                items={filtered}
                visible={visible}
                onLoadMore={() => setVisible((v) => v + PAGE_SIZE)}
              />
            </div>
            <aside className="lg:col-span-4" aria-label="排行榜与标签">
              <div className="lg:sticky lg:top-20">
                <HomeAside />
              </div>
            </aside>
          </div>
        </div>
      </section>

      <FaqSection />
      <BrandIntro />
    </>
  );
}